import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface AlertDefinition {
  id: string;
  organizationId: string;
  divisionId: string | null;
  regionId: string | null;
  deviceId: string | null;
  name: string;
  description: string | null;
  taskType: 'metric_alert' | 'device_audit' | 'custom_analysis';
  severity: string;
  intervalSeconds: number;
  enabled: boolean;
  config: Record<string, unknown>;
  actions: Record<string, unknown>[];
  lastRunAt: string | null;
  lastManualRunAt: string | null;
  lastResult: string | null;
  lastError: string | null;
  consecutiveFailures: number;
  alertState: 'ok' | 'alerting' | 'pending';
  alertStateChangedAt: string | null;
  source: 'system_default' | 'user_created';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface AlertDefinitionListResponse {
  tasks: AlertDefinition[];
  total: number;
}

export interface CreateAlertDefinitionRequest {
  name: string;
  description?: string;
  taskType: string;
  severity?: string;
  intervalSeconds: number;
  config: Record<string, unknown>;
  actions?: Record<string, unknown>[];
  divisionId?: string;
  regionId?: string;
  deviceId?: string;
}

export interface UpdateAlertDefinitionRequest {
  name?: string;
  description?: string;
  severity?: string;
  intervalSeconds?: number;
  config?: Record<string, unknown>;
  actions?: Record<string, unknown>[];
}

export interface ListAlertDefinitionsFilters {
  taskType?: string;
  enabled?: boolean;
}

const backendApi = config.backendApi;

const fetchAlertDefinitions = async (filters?: ListAlertDefinitionsFilters): Promise<AlertDefinition[]> => {
  const params = new URLSearchParams();
  if (filters?.taskType) params.append('task_type', filters.taskType);
  if (filters?.enabled !== undefined) params.append('enabled', String(filters.enabled));

  const queryString = params.toString();
  const url = `${backendApi}/api/alert-definitions${queryString ? `?${queryString}` : ''}`;
  const response = await axios.get<AlertDefinitionListResponse>(url, { withCredentials: true });
  return response.data.tasks;
};

const fetchAlertDefinition = async (taskId: string): Promise<AlertDefinition> => {
  const response = await axios.get<AlertDefinition>(
    `${backendApi}/api/alert-definitions/${taskId}`,
    { withCredentials: true }
  );
  return response.data;
};

const createAlertDefinition = async (data: CreateAlertDefinitionRequest): Promise<AlertDefinition> => {
  const response = await axios.post<AlertDefinition>(
    `${backendApi}/api/alert-definitions`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

const updateAlertDefinition = async (
  taskId: string,
  data: UpdateAlertDefinitionRequest
): Promise<AlertDefinition> => {
  const response = await axios.put<AlertDefinition>(
    `${backendApi}/api/alert-definitions/${taskId}`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

/// One incident a check currently has open, as shown in the pause/delete dialog.
export interface OpenIncidentSummary {
  id: string;
  title: string;
  severity: string;
  deviceId: string | null;
  createdAt: string;
}

export interface OpenIncidentsResponse {
  total: number;
  incidents: OpenIncidentSummary[];
}

const fetchOpenIncidents = async (taskId: string): Promise<OpenIncidentsResponse> => {
  const response = await axios.get<OpenIncidentsResponse>(
    `${backendApi}/api/alert-definitions/${taskId}/open-incidents`,
    { withCredentials: true },
  );
  return response.data;
};

/**
 * What a check currently has open, asked before pausing or deleting it.
 *
 * Only fetched once the dialog is actually open: it is a question about a decision the user
 * is in the middle of making, not something worth loading for every row of a list.
 */
export function useAlertDefinitionOpenIncidents(taskId: string | null) {
  return useQuery({
    queryKey: ['alert-definition-open-incidents', taskId],
    queryFn: () => {
      if (!taskId) throw new Error('taskId required');
      return fetchOpenIncidents(taskId);
    },
    enabled: !!taskId,
    // The user is looking at this list to make a decision; a cached answer from an earlier
    // dialog could name incidents that have since closed.
    staleTime: 0,
  });
}

const deleteAlertDefinition = async (taskId: string, resolveIncidents: boolean): Promise<void> => {
  await axios.delete(`${backendApi}/api/alert-definitions/${taskId}`, {
    params: { resolveIncidents },
    withCredentials: true,
  });
};

const enableAlertDefinition = async (taskId: string): Promise<AlertDefinition> => {
  const response = await axios.post<AlertDefinition>(
    `${backendApi}/api/alert-definitions/${taskId}/enable`,
    {},
    { withCredentials: true }
  );
  return response.data;
};

const disableAlertDefinition = async (taskId: string, resolveIncidents: boolean): Promise<AlertDefinition> => {
  const response = await axios.post<AlertDefinition>(
    `${backendApi}/api/alert-definitions/${taskId}/disable`,
    {},
    { params: { resolveIncidents }, withCredentials: true }
  );
  return response.data;
};

export class ExecuteCooldownError extends Error {
  retryAfterSeconds: number;
  constructor(message: string, retryAfterSeconds: number) {
    super(message);
    this.name = 'ExecuteCooldownError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

const executeAlertDefinition = async (taskId: string): Promise<AlertDefinition> => {
  try {
    const response = await axios.post<AlertDefinition>(
      `${backendApi}/api/alert-definitions/${taskId}/execute`,
      {},
      { withCredentials: true }
    );
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 429) {
      const body = err.response.data as { message?: string; retryAfterSeconds?: number };
      const retryAfter = body.retryAfterSeconds ?? (Number(err.response.headers['retry-after']) || 60);
      throw new ExecuteCooldownError(body.message ?? `Please wait ${String(retryAfter)}s before re-running.`, retryAfter);
    }
    throw err;
  }
};

export function useAlertDefinitions(
  organizationId: string | null | undefined,
  filters?: ListAlertDefinitionsFilters
) {
  const contextReady = useActiveContextReady();

  return useQuery<AlertDefinition[]>({
    queryKey: ['alert-definitions', organizationId, filters],
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId required');
      return fetchAlertDefinitions(filters);
    },
    enabled: !!organizationId && contextReady,
  });
}

export function useAlertDefinition(taskId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<AlertDefinition>({
    queryKey: ['alert-definition', taskId],
    queryFn: () => {
      if (!taskId) throw new Error('taskId required');
      return fetchAlertDefinition(taskId);
    },
    enabled: !!taskId && contextReady,
  });
}

export function useCreateAlertDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAlertDefinitionRequest) => createAlertDefinition(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alert-definitions'] });
    },
  });
}

export function useUpdateAlertDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: UpdateAlertDefinitionRequest }) =>
      updateAlertDefinition(taskId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['alert-definition', variables.taskId] });
      void queryClient.invalidateQueries({ queryKey: ['alert-definitions'] });
    },
  });
}

export function useDeleteAlertDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, resolveIncidents }: { taskId: string; resolveIncidents: boolean }) =>
      deleteAlertDefinition(taskId, resolveIncidents),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alert-definitions'] });
      // Incidents may have been resolved alongside the check.
      void queryClient.invalidateQueries({ queryKey: ['incidents'] });
    },
  });
}

export function useEnableAlertDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => enableAlertDefinition(taskId),
    onSuccess: (_, taskId) => {
      void queryClient.invalidateQueries({ queryKey: ['alert-definition', taskId] });
      void queryClient.invalidateQueries({ queryKey: ['alert-definitions'] });
    },
  });
}

export function useDisableAlertDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, resolveIncidents }: { taskId: string; resolveIncidents: boolean }) =>
      disableAlertDefinition(taskId, resolveIncidents),
    onSuccess: (_, { taskId }) => {
      void queryClient.invalidateQueries({ queryKey: ['alert-definition', taskId] });
      void queryClient.invalidateQueries({ queryKey: ['alert-definitions'] });
      void queryClient.invalidateQueries({ queryKey: ['incidents'] });
    },
  });
}

export function useExecuteAlertDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => executeAlertDefinition(taskId),
    onSuccess: (_, taskId) => {
      void queryClient.invalidateQueries({ queryKey: ['alert-definition', taskId] });
      void queryClient.invalidateQueries({ queryKey: ['alert-definitions'] });
    },
  });
}
